import { useState, useCallback } from 'react';
import { bannerService } from '../../../../app/services/api/content/bannerService';
import {
  BannerSummary,
  BannerDetail,
  BannerSlot,
  CreateBannerRequest,
  UpdateBannerRequest,
  AssignBannerToSlotRequest,
  CreateSlotRequest,
  UpdateSlotRequest,
} from '../types/BannerAdmin';

const useBannerAdmin = () => {
  const [banners, setBanners] = useState<BannerSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [slots, setSlots] = useState<BannerSlot[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchBanners = useCallback(async (params?: { search?: string; page?: number; pageSize?: number }) => {
    setLoading(true);
    try {
      const result = await bannerService.banners.getAll(params);
      setBanners(result.items);
      setTotalCount(result.totalCount);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSlots = useCallback(async () => {
    const data = await bannerService.slots.getAll();
    setSlots(data);
    return data;
  }, []);

  const getBanner = useCallback(async (id: number): Promise<BannerDetail> => {
    return bannerService.banners.getById(id);
  }, []);

  const createBanner = useCallback(async (request: CreateBannerRequest): Promise<{ id: number }> => {
    return bannerService.banners.create(request);
  }, []);

  const updateBanner = useCallback(async (id: number, request: UpdateBannerRequest) => {
    await bannerService.banners.update(id, request);
  }, []);

  const deleteBanner = useCallback(async (id: number) => {
    await bannerService.banners.delete(id);
  }, []);

  const assignBannerToSlot = useCallback(async (request: AssignBannerToSlotRequest) => {
    await bannerService.assignments.assign(request);
  }, []);

  const removeAssignment = useCallback(async (assignmentId: number) => {
    await bannerService.assignments.remove(assignmentId);
  }, []);

  const uploadBannerImage = useCallback(async (file: File): Promise<string> => {
    return bannerService.banners.uploadImage(file);
  }, []);

  const createSlot = useCallback(async (request: CreateSlotRequest): Promise<{ id: number }> => {
    return bannerService.slots.create(request);
  }, []);

  const updateSlot = useCallback(async (id: number, request: UpdateSlotRequest) => {
    await bannerService.slots.update(id, request);
  }, []);

  const deleteSlot = useCallback(async (id: number) => {
    await bannerService.slots.delete(id);
  }, []);

  return {
    banners,
    totalCount,
    slots,
    loading,
    fetchBanners,
    fetchSlots,
    getBanner,
    createBanner,
    updateBanner,
    deleteBanner,
    assignBannerToSlot,
    removeAssignment,
    uploadBannerImage,
    createSlot,
    updateSlot,
    deleteSlot,
  };
};

export default useBannerAdmin;
